// Workspace / region switcher — a global filter that partitions the app into
// named workspaces (e.g. "South FL" and "Central FL"). Super Admin sees a
// top-bar dropdown and their pick propagates to every API list call and every
// map re-center. Non super_admin users are locked to their own region on the
// backend, so the FE just displays a read-only badge.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const RegionContext = createContext(null);

export const REGIONS = [
  {
    id: "south_fl",
    label: "South FL",
    short: "South FL",
    // Miami-Dade + Broward midpoint (Aventura / Hallandale corridor)
    center: [25.95, -80.20],
    zoom: 10,
  },
  {
    id: "central_fl",
    label: "Central FL",
    short: "Central FL",
    center: [28.54, -81.38],
    zoom: 11,
  },
];

export const ALL_REGION = {
  id: "all",
  label: "All regions",
  short: "All",
  // "All" defaults to the geographic mid-point between the two workspaces
  // so both clusters are visible on first load.
  center: [27.3, -80.85],
  zoom: 8,
  // Explicit bounds so the map spans from Leesburg (top of Central FL) down
  // to Homestead (bottom of South FL) — Super Admin can eyeball both areas
  // at once instead of hunting between workspaces.
  // Padding included on the north/south/west edges so no marker gets clipped.
  bounds: [
    [29.10, -82.20], // NW: north of Leesburg, west of Ocala
    [25.30, -80.05], // SE: south of Homestead, offshore edge of Miami-Dade
  ],
};

const LS_KEY = "pr_workspace";

export function useRegion() {
  return useContext(RegionContext);
}

export const RegionProvider = ({ children }) => {
  const { user } = useAuth();
  const [selected, setSelected] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) || "south_fl";
    } catch {
      return "south_fl";
    }
  });

  // Non super_admin users are pinned to whatever region their user record
  // carries — the backend enforces this too, but we mirror it in the UI so the
  // switcher shows the correct label instead of stale localStorage.
  useEffect(() => {
    if (user && user.role !== "super_admin") {
      setSelected(user.region || "south_fl");
    }
  }, [user]);

  const setRegion = (id) => {
    setSelected(id);
    try {
      localStorage.setItem(LS_KEY, id);
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  };

  const isSuperAdmin = user?.role === "super_admin";
  const canSwitch = isSuperAdmin;

  const active = useMemo(() => {
    if (selected === "all") return ALL_REGION;
    return REGIONS.find((r) => r.id === selected) || REGIONS[0];
  }, [selected]);

  const value = useMemo(
    () => ({
      selected,       // "all" | "south_fl" | "central_fl"
      setRegion,
      canSwitch,      // only Super Admin
      active,         // { id, label, center, zoom }
      REGIONS,
      ALL_REGION,
    }),
    [selected, canSwitch, active]
  );

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

// Small helper for building a `?region=<id>` query param. Returns an empty
// string when the current workspace is "all" so the API call is unfiltered.
export function regionQueryParam(selected) {
  if (!selected || selected === "all") return "";
  return `region=${encodeURIComponent(selected)}`;
}
